import { useEffect, useState } from 'react';
import Modal from '../components/common/Modal';
import { vendorApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Store, Loader2, Package,
  CheckCircle2, XCircle, Upload, Download, Info,
  CalendarDays, History, Truck, Repeat,
} from 'lucide-react';

// Vendor pricing is INR-only (Venky 2026-05-14); USD just in case anyone
// imported it earlier. Fallback to the raw code if unknown.
function currencySymbol(code) {
  if (!code) return '₹';
  const c = String(code).toUpperCase();
  if (c === 'INR') return '₹';
  if (c === 'USD') return '$';
  return `${c} `;
}

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [detailVendor, setDetailVendor] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await vendorApi.list();
      setVendors(Array.isArray(res.data) ? res.data : (res.data.vendors || []));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load vendors');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div>

      <div className="p-6">
        <div className="flex items-center justify-end mb-4">
          <button onClick={() => setCreateOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New vendor
          </button>
        </div>

        {loading ? (
          // Skeleton grid — 6 grey cards so the viewport is obviously
          // populated while the fetch runs. The previous tiny spinner made
          // the page look blank on first load (Kalpana UAT feedback).
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" aria-busy="true" aria-label="Loading vendors">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                    <div className="h-2 bg-gray-100 rounded w-1/3" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 bg-gray-100 rounded w-full" />
                  <div className="h-2 bg-gray-100 rounded w-4/5" />
                </div>
              </div>
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm bg-white rounded-xl border border-gray-200">
            No vendors yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {vendors.map((v) => (
              <div key={v.id} className={`bg-white rounded-xl border p-4 hover:shadow-sm transition ${v.is_default ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-semibold"
                         style={{ backgroundColor: v.color || '#3b82f6' }}>
                      {v.code?.slice(0, 3).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-1.5">
                        {v.name}
                        {v.is_default && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">DEFAULT</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{v.code}</div>
                    </div>
                  </div>
                  <button onClick={() => setEditVendor(v)} title="Edit"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button onClick={async () => {
                  try {
                    await vendorApi.setDefault(v.id, !v.is_default);
                    toast.success(v.is_default ? 'Cleared default vendor' : `${v.name} is now the default vendor`);
                    load();
                  } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                }}
                        className={`text-xs px-2 py-1 rounded mb-2 ${v.is_default ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`}>
                  {v.is_default ? '★ Default vendor (click to clear)' : '☆ Set as default'}
                </button>
                <div className="text-xs text-gray-600 space-y-0.5">
                  {v.contact_name && <div><span className="text-gray-400">Contact:</span> {v.contact_name}</div>}
                  {v.contact_email && <div><span className="text-gray-400">Email:</span> {v.contact_email}</div>}
                  {v.contact_phone && <div><span className="text-gray-400">Phone:</span> {v.contact_phone}</div>}
                </div>
                <PackageShippingEditor vendor={v} onUpdated={load} />
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <button onClick={() => setDetailVendor(v)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    <Package className="w-3.5 h-3.5" /> Products &amp; pricing
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <VendorFormModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onDone={load} />
      )}
      {editVendor && (
        <VendorFormModal isOpen={!!editVendor} onClose={() => setEditVendor(null)} onDone={load}
                         vendor={editVendor} />
      )}
      {detailVendor && (
        <VendorDetailModal isOpen={!!detailVendor} onClose={() => setDetailVendor(null)}
                           vendor={detailVendor} />
      )}
    </div>
  );
}

// Inline editor for vendor's per-package shipping rate (Venky / Kalpana
// 2026-05-14). Click to enter edit mode, type the new ₹ amount, hit save.
function PackageShippingEditor({ vendor, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(vendor.package_shipping_cost ?? '0'));
  const [saving, setSaving] = useState(false);

  async function save() {
    const amount = Number(value);
    if (!isFinite(amount) || amount < 0) { toast.error('Enter a non-negative amount'); return; }
    setSaving(true);
    try {
      await vendorApi.setPackageShipping(vendor.id, amount);
      toast.success('Shipping rate updated');
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  if (!editing) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs">
        <span className="text-gray-500">Shipping per package:</span>
        <span className="font-semibold text-gray-900">
          ₹{Number(vendor.package_shipping_cost || 0).toFixed(2)}
        </span>
        <button onClick={() => { setValue(String(vendor.package_shipping_cost ?? '0')); setEditing(true); }}
                className="text-blue-600 hover:underline">
          edit
        </button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-1 text-xs">
      <span className="text-gray-500">Shipping per package: ₹</span>
      <input type="number" step="0.01" value={value} autoFocus
             onChange={(e) => setValue(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
             className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs" />
      <button onClick={save} disabled={saving}
              className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
        {saving ? '…' : 'save'}
      </button>
      <button onClick={() => setEditing(false)}
              className="text-gray-500 hover:underline">cancel</button>
    </div>
  );
}

function VendorFormModal({ isOpen, onClose, onDone, vendor }) {
  const isEdit = !!vendor;
  const [form, setForm] = useState({
    name:         vendor?.name || '',
    code:         vendor?.code || '',
    contactName:  vendor?.contact_name || '',
    contactEmail: vendor?.contact_email || '',
    contactPhone: vendor?.contact_phone || '',
    address:      vendor?.address || '',
    color:        vendor?.color || '#3b82f6',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name.trim() || (!isEdit && !form.code.trim())) {
      toast.error('Name and code are required'); return;
    }
    setSaving(true);
    try {
      if (isEdit) await vendorApi.update(vendor.id, form);
      else await vendorApi.create(form);
      toast.success(isEdit ? 'Vendor updated' : 'Vendor created');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit vendor' : 'New vendor'}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Name *</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Code *</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                   disabled={isEdit}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:bg-gray-50" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Contact name</span>
          <input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Email</span>
            <input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                   type="email"
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Phone</span>
            <input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Address</span>
          <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Badge color</span>
          <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                 className="h-9 w-16 border border-gray-300 rounded" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const RESHIP_REASON_LABELS = {
  customs_hold: 'Stuck in customs',
  lost: 'Lost in transit',
  damaged: 'Damaged parcel',
  missing_pills: 'Missing pills / short qty',
  wrong_product: 'Wrong product delivered',
  customer_feedback: 'Customer dissatisfied',
  delivered_want_another: 'Delivered fine — wants another',
  delivered_not_received: 'Delivered — customer says not received',
  other: 'Other',
};

function ReshipPolicyPanel({ vendor, onClose }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [overrides, setOverrides] = useState({ secondReshipPct: 100, fullPriceProducts: '' });
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [view, setView] = useState('current'); // current | history
  const [history, setHistory] = useState([]);

  async function load() {
    try {
      const [res, hist] = await Promise.all([
        vendorApi.reshipPolicies(vendor.id),
        vendorApi.reshipPolicyHistory(vendor.id),
      ]);
      setData(res.data);
      setHistory(Array.isArray(hist.data) ? hist.data : []);
      setOverrides({
        secondReshipPct: res.data.secondReshipPct,
        fullPriceProducts: (res.data.fullPriceProducts || []).join(', '),
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load reship policy');
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor.id]);

  async function saveRow(reason, productCostPct, shippingCostPct) {
    if (!effectiveFrom || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) {
      toast.error('Pick an effective-from date first'); return;
    }
    setSaving(reason);
    try {
      await vendorApi.updateReshipPolicy(vendor.id, reason, { productCostPct, shippingCostPct, effectiveFrom });
      toast.success(`${RESHIP_REASON_LABELS[reason] || reason} — new % effective ${effectiveFrom}; older dispatches keep the old %`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(null); }
  }

  async function saveOverrides() {
    setSaving('overrides');
    try {
      await vendorApi.updateReshipOverrides(vendor.id, overrides);
      toast.success('Overrides saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(null); }
  }

  if (!data) return <div className="py-4 text-center text-gray-500 text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="font-semibold text-gray-700">
          Reship billing policy
          <span className="font-normal text-gray-500"> — what {vendor.name} charges us when we ask for a reship. Used by Vendor Payables.</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded-lg text-[11px]"
                 title="The date a saved % takes over. Parcels dispatched before this date keep billing at the old %.">
            <CalendarDays className="w-3.5 h-3.5 text-gray-600" />
            <span className="text-gray-600">Effective from</span>
            <input type="date" value={effectiveFrom}
                   onChange={(e) => setEffectiveFrom(e.target.value)}
                   className="px-1 py-0.5 text-[11px] border-0 focus:outline-none focus:ring-0 bg-transparent font-medium" />
          </label>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-[11px]">close</button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-2">
        {[['current', 'Current policy'], ['history', `Change history (${history.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium ${view === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {view === 'current' && (
      <table className="w-full text-xs border border-gray-200 rounded">
        <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
          <tr>
            <th className="text-left px-2 py-1.5">Reship reason</th>
            <th className="text-right px-2 py-1.5">Product cost %</th>
            <th className="text-right px-2 py-1.5">Shipping %</th>
            <th className="text-left px-2 py-1.5">Effective</th>
            <th className="px-2 py-1.5 w-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.policies.map((p) => (
            <PolicyRow key={`${p.reason}|${p.effective_from}`} policy={p} saving={saving === p.reason} onSave={saveRow} />
          ))}
        </tbody>
      </table>
      )}

      {view === 'history' && (
        <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5">Reship reason</th>
                <th className="text-right px-2 py-1.5">Product %</th>
                <th className="text-right px-2 py-1.5">Shipping %</th>
                <th className="text-left px-2 py-1.5">In force</th>
                <th className="text-left px-2 py-1.5">Changed by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map((r, i) => (
                <tr key={i} className={r.effective_to == null ? '' : 'text-gray-500 bg-gray-50/60'}>
                  <td className="px-2 py-1.5 font-medium text-gray-800">{RESHIP_REASON_LABELS[r.reason] || r.reason}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.product_cost_pct == null ? 'vendor decides' : `${Number(r.product_cost_pct)}%`}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Number(r.shipping_cost_pct)}%</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {fmtEffective(r.effective_from, r.effective_to)}
                    {r.effective_to == null && <span className="ml-1 text-emerald-700 text-[10px] font-medium uppercase">current</span>}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">
                    {r.changed_by_name || 'initial setup'}
                    {r.updated_at && <span className="text-gray-400"> · {new Date(r.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-2">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">Second reship — product %</span>
          <input type="number" min="0" max="100" value={overrides.secondReshipPct}
                 onChange={(e) => setOverrides({ ...overrides, secondReshipPct: e.target.value })}
                 className="w-24 px-2 py-1 border border-gray-300 rounded text-xs" />
        </label>
        <label className="block flex-1 min-w-[14rem]">
          <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
            Always full price products (comma-separated, name contains)
          </span>
          <input value={overrides.fullPriceProducts}
                 onChange={(e) => setOverrides({ ...overrides, fullPriceProducts: e.target.value })}
                 placeholder="rybelsus"
                 className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
        </label>
        <button onClick={saveOverrides} disabled={saving === 'overrides'}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-[11px] font-medium hover:bg-blue-700 disabled:opacity-50">
          Save overrides
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        Blank product % = vendor decides per case — those reship lines appear as
        "needs review" in Vendor Payables instead of billing ₹0 or 100%. A second
        reship of the same order and any "always full price" product bill at full
        price regardless of reason.
      </p>
    </div>
  );
}

function PolicyRow({ policy, saving, onSave }) {
  const [pp, setPp] = useState(policy.product_cost_pct == null ? '' : Number(policy.product_cost_pct));
  const [sp, setSp] = useState(Number(policy.shipping_cost_pct));
  const dirty = String(pp) !== String(policy.product_cost_pct == null ? '' : Number(policy.product_cost_pct))
    || Number(sp) !== Number(policy.shipping_cost_pct);
  return (
    <tr>
      <td className="px-2 py-1.5">
        <span className="font-medium text-gray-800">{RESHIP_REASON_LABELS[policy.reason] || policy.reason}</span>
        {policy.notes && <span className="text-gray-400"> — {policy.notes}</span>}
      </td>
      <td className="px-2 py-1.5 text-right">
        <input type="number" min="0" max="100" value={pp} placeholder="vendor decides"
               onChange={(e) => setPp(e.target.value)}
               className="w-28 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
      </td>
      <td className="px-2 py-1.5 text-right">
        <input type="number" min="0" max="100" value={sp}
               onChange={(e) => setSp(e.target.value)}
               className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
      </td>
      <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap text-[11px]">
        {fmtEffective(policy.effective_from, policy.effective_to)}
      </td>
      <td className="px-2 py-1.5 text-right">
        {dirty && (
          <button onClick={() => onSave(policy.reason, pp === '' ? null : pp, sp)} disabled={saving}
                  className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px] font-medium hover:bg-emerald-700 disabled:opacity-50">
            {saving ? '…' : 'Save'}
          </button>
        )}
      </td>
    </tr>
  );
}

function fmtEffective(from, to) {
  const f = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  // The 2000-01-01 sentinel marks the baseline card — show it as open-ended.
  const fromLabel = String(from).slice(0, 10) <= '2000-01-01' ? 'start' : f(from);
  return `${fromLabel} → ${to ? f(to) : 'onwards'}`;
}

function VendorDetailModal({ isOpen, onClose, vendor }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addProdOpen, setAddProdOpen] = useState(false);
  const [pricingProd, setPricingProd] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showCsvHelp, setShowCsvHelp] = useState(false);
  const [imports, setImports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [showShipping, setShowShipping] = useState(false);
  const [shippingUploading, setShippingUploading] = useState(false);
  const [shippingResult, setShippingResult] = useState(null);
  const [shippingEffectiveFrom, setShippingEffectiveFrom] = useState(todayIso());
  const [shippingView, setShippingView] = useState('current'); // current | changes | uploads
  const [shippingHistory, setShippingHistory] = useState([]);
  const [shippingImports, setShippingImports] = useState([]);
  const [shippingSearch, setShippingSearch] = useState('');
  const [showReshipPolicy, setShowReshipPolicy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await vendorApi.products(vendor.id);
      setProducts(Array.isArray(res.data) ? res.data : (res.data.products || []));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setLoading(false); }
  }
  async function loadImports() {
    try {
      const res = await vendorApi.pricingImports(vendor.id, { limit: 50 });
      setImports(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Silent — history panel is non-blocking.
    }
  }
  async function loadShippingRates() {
    try {
      const [cur, hist, imps] = await Promise.all([
        vendorApi.shippingRates(vendor.id),
        vendorApi.shippingRateHistory(vendor.id),
        vendorApi.shippingImports(vendor.id),
      ]);
      setShippingRates(Array.isArray(cur.data) ? cur.data : []);
      setShippingHistory(Array.isArray(hist.data) ? hist.data : []);
      setShippingImports(Array.isArray(imps.data) ? imps.data : []);
    } catch {
      // Silent — panel is non-blocking.
    }
  }
  useEffect(() => { load(); loadImports(); loadShippingRates(); /* eslint-disable-next-line */ }, [vendor.id]);

  async function uploadShippingRates(file) {
    if (!file) return;
    if (!shippingEffectiveFrom || !/^\d{4}-\d{2}-\d{2}$/.test(shippingEffectiveFrom)) {
      toast.error('Pick an effective-from date first'); return;
    }
    setShippingUploading(true);
    setShippingResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('effective_from', shippingEffectiveFrom);
      const res = await vendorApi.importShippingRates(vendor.id, fd);
      setShippingResult(res.data);
      const { imported, defaultsAdded, conflicts, effectiveFrom: ef } = res.data || {};
      toast.success(`New rate card effective ${ef}: ${imported} destination(s), ${defaultsAdded} country default(s)${conflicts?.length ? `, ${conflicts.length} conflict(s) — see panel` : ''}`);
      loadShippingRates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rate card upload failed');
    } finally { setShippingUploading(false); }
  }

  async function toggleAvail(p) {
    try {
      await vendorApi.setProductAvailability(p.id, { isAvailable: !p.is_available });
      toast.success('Availability updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  }

  async function uploadBulk() {
    if (!bulkFile) { toast.error('Choose a CSV file first'); return; }
    if (!effectiveFrom || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) {
      toast.error('Pick an effective-from date first'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('effective_from', effectiveFrom);
      const res = await vendorApi.bulkUploadPricing(vendor.id, fd);
      setBulkResult(res.data);
      const { rowsParsed, productsAdded, productsUpdated, pricingRowsCreated, errors } = res.data || {};
      if (errors?.length) {
        toast.error(`Parsed ${rowsParsed}, added ${productsAdded}, ${errors.length} row(s) failed — see panel`);
      } else {
        toast.success(`Imported ${pricingRowsCreated} pricing row(s): ${productsAdded} new, ${productsUpdated} updated`);
      }
      setBulkFile(null);
      load();
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk upload failed');
    } finally { setUploading(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${vendor.name} — products & pricing`} size="xl">
      <div className="space-y-3">
        {/* Explicit scope banner — Kalpana asked for this 2026-05-14 so it's
            never ambiguous which vendor a CSV/XLSX upload is being imported
            against. The modal you open IS the vendor you're importing for. */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900 flex items-center gap-2">
          <Store className="w-3.5 h-3.5 shrink-0" />
          <span>
            All actions here apply to <strong>{vendor.name}</strong> only.
            Close this dialog and open a different vendor card to upload its prices.
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => downloadAuthed('/vendors/pricing-sample.csv', `${vendor.name}-pricing-sample.csv`)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50">
              <Download className="w-3.5 h-3.5" /> Sample CSV
            </button>
            <label className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> {bulkFile ? bulkFile.name : 'Choose CSV / XLSX'}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                     onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
            </label>
            <label className="inline-flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs"
                   title="The date this price list takes effect. Past, today, or future. Past-dated uploads back-fill history; future-dated ones become live on that day.">
              <CalendarDays className="w-3.5 h-3.5 text-gray-600" />
              <span className="text-gray-600">Effective from</span>
              <input type="date" value={effectiveFrom}
                     onChange={(e) => setEffectiveFrom(e.target.value)}
                     className="px-1 py-0.5 text-xs border-0 focus:outline-none focus:ring-0 bg-transparent font-medium" />
            </label>
            {bulkFile && (
              <button onClick={uploadBulk} disabled={uploading}
                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Import prices to {vendor.name}
              </button>
            )}
            <button onClick={() => setShowCsvHelp((v) => !v)}
                    title="How to fill the CSV"
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border ${showCsvHelp ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'}`}>
              <Info className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowHistory((v) => !v)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-xs font-medium ${showHistory ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <History className="w-3.5 h-3.5" /> Upload history {imports.length > 0 && (<span className="ml-1 text-[10px] opacity-80">({imports.length})</span>)}
            </button>
            <button onClick={() => setShowShipping((v) => !v)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-xs font-medium ${showShipping ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <Truck className="w-3.5 h-3.5" /> Shipping rates {shippingRates.length > 0 && (<span className="ml-1 text-[10px] opacity-80">({shippingRates.length})</span>)}
            </button>
            <button onClick={() => setShowReshipPolicy((v) => !v)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-xs font-medium ${showReshipPolicy ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <Repeat className="w-3.5 h-3.5" /> Reship policy
            </button>
          </div>
          <button onClick={() => setAddProdOpen(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
            <Plus className="w-3.5 h-3.5" /> Add product
          </button>
        </div>

        {showCsvHelp && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs text-gray-800 space-y-3">
            <div className="flex items-start justify-between">
              <div className="font-semibold text-blue-900 text-sm">CSV format guide</div>
              <button onClick={() => setShowCsvHelp(false)}
                      className="text-blue-700 hover:underline text-xs">close</button>
            </div>

            <div>
              <div className="font-medium text-gray-900 mb-1">Columns</div>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="py-1 pr-3">Column</th>
                    <th className="py-1 pr-3">Required?</th>
                    <th className="py-1 pr-3">If left blank</th>
                    <th className="py-1">Notes</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-t border-blue-100">
                    <td className="py-1 pr-3 font-mono text-[11px]">product_name</td>
                    <td className="py-1 pr-3 text-rose-700 font-semibold">Yes</td>
                    <td className="py-1 pr-3">row rejected</td>
                    <td className="py-1">Must match the product name as it appears on MBO uploads exactly (case + spelling).</td>
                  </tr>
                  <tr className="border-t border-blue-100">
                    <td className="py-1 pr-3 font-mono text-[11px]">brand_name</td>
                    <td className="py-1 pr-3 text-gray-600">No</td>
                    <td className="py-1 pr-3">stays empty / preserved</td>
                    <td className="py-1">Indian brand for display. Refreshed on re-upload only if provided.</td>
                  </tr>
                  <tr className="border-t border-blue-100">
                    <td className="py-1 pr-3 font-mono text-[11px]">manufacturer</td>
                    <td className="py-1 pr-3 text-gray-600">No</td>
                    <td className="py-1 pr-3">stays empty / preserved</td>
                    <td className="py-1">For reference.</td>
                  </tr>
                  <tr className="border-t border-blue-100">
                    <td className="py-1 pr-3 font-mono text-[11px]">unit_price</td>
                    <td className="py-1 pr-3 text-rose-700 font-semibold">Yes</td>
                    <td className="py-1 pr-3">row rejected</td>
                    <td className="py-1">Per-unit price in ₹. Numeric, no symbol. E.g. <code>42.00</code></td>
                  </tr>
                  <tr className="border-t border-blue-100">
                    <td className="py-1 pr-3 font-mono text-[11px]">currency</td>
                    <td className="py-1 pr-3 text-gray-600">No</td>
                    <td className="py-1 pr-3">defaults to <strong>INR</strong></td>
                    <td className="py-1">Vendor pricing is INR by default — leave blank.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="rounded bg-white border border-blue-100 p-3">
              <div className="font-medium text-gray-900 mb-1">Re-uploads preserve history</div>
              <p>The <strong>Effective from</strong> date you pick is when these prices become live. Earlier prices for the same product are kept and end the day before — so end-of-month vendor payments use the price that was in effect on each order's dispatch date.</p>
              <div className="mt-2 text-[11px] text-gray-700">
                <strong>Example:</strong> Tadalafil was ₹40, effective 1 Apr. FRT raises it to ₹42 from 1 Jun — upload the new file with <strong>Effective from 1 Jun</strong>. The ₹40 row now reads "1 Apr → 31 May"; a new ₹42 row covers "1 Jun → onwards". An order shipped on 15 May still bills at ₹40; an order shipped on 5 Jun bills at ₹42.
              </div>
              <div className="mt-2 text-[11px] text-gray-700">
                <strong>Past or future dates are fine.</strong> Past = back-fill a correction. Future = scheduled price change. Same-day re-uploads (fixing a typo) just overwrite the row you just created.
              </div>
            </div>

            <div className="rounded bg-white border border-blue-100 p-3">
              <div className="font-medium text-gray-900 mb-1">Practical tips</div>
              <ul className="list-disc pl-5 space-y-0.5">
                <li><strong>Product name match matters:</strong> spell it the same way MBO uploads spell it — that's how the system maps prices to dispatched orders.</li>
                <li><strong>Currency:</strong> leave blank — INR is the default.</li>
                <li><strong>Shipping cost is not in this CSV.</strong> Vendor shipping is one flat rate per package — set it on the vendor card itself ("Shipping per package: ₹X · edit").</li>
                <li><strong>Errors don't block the batch:</strong> if a single row has a problem (missing price, etc.), only that row is rejected — other rows still import. The result panel shows row numbers + errors.</li>
              </ul>
            </div>
          </div>
        )}

        {bulkResult && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="font-semibold text-gray-700 mb-1">
              Last import{bulkResult.effectiveFrom && (
                <span className="ml-2 text-[11px] font-normal text-gray-500">
                  effective from {bulkResult.effectiveFrom}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
              <span>Parsed: <strong>{bulkResult.rowsParsed}</strong></span>
              <span>New products: <strong>{bulkResult.productsAdded}</strong></span>
              <span>New price rows: <strong>{bulkResult.pricingRowsCreated}</strong></span>
              <span>Old rows closed: <strong>{bulkResult.pricingRowsClosed}</strong></span>
              <span>Unchanged: <strong>{bulkResult.pricingUnchanged}</strong></span>
            </div>
            {(bulkResult.mappedAuto != null || bulkResult.needsManualMapping != null) && (
              <div className="grid grid-cols-2 gap-2 border-t border-gray-200 pt-2">
                <span className="text-emerald-700">
                  Auto-mapped to master: <strong>{bulkResult.mappedAuto || 0}</strong>
                </span>
                <span className="text-amber-700">
                  Needs manual mapping: <strong>{bulkResult.needsManualMapping || 0}</strong>
                  {bulkResult.needsManualMapping > 0 && (
                    <a href="/admin/vendor-mapping" className="ml-2 text-blue-600 hover:underline">
                      → resolve queue
                    </a>
                  )}
                </span>
              </div>
            )}
            {bulkResult.errors?.length > 0 && (
              <div className="mt-2 text-rose-700">
                <div className="font-medium">{bulkResult.errors.length} row(s) had errors:</div>
                <ul className="list-disc pl-4 mt-1">
                  {bulkResult.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                  {bulkResult.errors.length > 10 && (
                    <li>…and {bulkResult.errors.length - 10} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {showHistory && (
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-gray-700 flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-gray-600" /> Pricing upload history
              </div>
              <button onClick={() => setShowHistory(false)}
                      className="text-gray-500 hover:text-gray-700 text-[11px]">close</button>
            </div>
            {imports.length === 0 ? (
              <div className="py-6 text-center text-gray-500 border border-dashed border-gray-200 rounded">
                No uploads yet for this vendor.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Uploaded</th>
                      <th className="text-left px-2 py-1.5">File</th>
                      <th className="text-left px-2 py-1.5">Effective from</th>
                      <th className="text-left px-2 py-1.5">By</th>
                      <th className="text-right px-2 py-1.5">Parsed</th>
                      <th className="text-right px-2 py-1.5">New rows</th>
                      <th className="text-right px-2 py-1.5">Closed</th>
                      <th className="text-right px-2 py-1.5">Failed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {imports.map((imp) => (
                      <tr key={imp.id}>
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">
                          {imp.uploadedAt ? new Date(imp.uploadedAt).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          }) : '—'}
                        </td>
                        <td className="px-2 py-1.5 max-w-[18rem] truncate" title={imp.sourceFilename || ''}>
                          {imp.sourceFilename || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap font-medium">
                          {imp.effectiveFrom ? new Date(imp.effectiveFrom).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          }) : '—'}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">
                          {imp.uploadedBy?.name || imp.uploadedBy?.email || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right">{imp.rowsParsed}</td>
                        <td className="px-2 py-1.5 text-right text-emerald-700 font-medium">{imp.pricingRowsCreated}</td>
                        <td className="px-2 py-1.5 text-right text-gray-700">{imp.pricingRowsClosed}</td>
                        <td className={`px-2 py-1.5 text-right ${imp.rowsFailed ? 'text-rose-700 font-medium' : 'text-gray-400'}`}>
                          {imp.rowsFailed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {showShipping && (
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-gray-700 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5 text-gray-600" /> Shipping rate card
                <span className="font-normal text-gray-500">
                  — per parcel, by destination. Used by Vendor Payables.
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => downloadAuthed('/vendors/shipping-rates-sample.csv', 'shipping-rates-sample.csv')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-[11px] font-medium hover:bg-gray-50">
                  <Download className="w-3.5 h-3.5" /> Sample file
                </button>
                <label className="inline-flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded-lg text-[11px]"
                       title="The date this rate card takes effect. The previous card keeps billing parcels dispatched before this date.">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-gray-600">Effective from</span>
                  <input type="date" value={shippingEffectiveFrom}
                         onChange={(e) => setShippingEffectiveFrom(e.target.value)}
                         className="px-1 py-0.5 text-[11px] border-0 focus:outline-none focus:ring-0 bg-transparent font-medium" />
                </label>
                <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-medium hover:bg-emerald-700 cursor-pointer">
                  {shippingUploading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Upload className="w-3.5 h-3.5" />}
                  Upload new rate card
                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                         onChange={(e) => { uploadShippingRates(e.target.files?.[0] || null); e.target.value = ''; }} />
                </label>
                <button onClick={() => setShowShipping(false)}
                        className="text-gray-500 hover:text-gray-700 text-[11px]">close</button>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">
              Columns: <strong>Country | Shipper | State | Shipping</strong> (₹ per parcel).
              Each upload becomes the card from its <strong>effective from</strong> date; the
              previous card is kept and still bills parcels dispatched before that date.
              Destinations listed twice with different prices keep the <strong>higher</strong> rate
              and are flagged. Countries get an automatic default row for orders without a state.
            </p>
            {shippingResult?.conflicts?.length > 0 && (
              <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                <strong>{shippingResult.conflicts.length} destination(s) had two different prices</strong> — kept the higher one. Confirm with the vendor:
                <ul className="mt-1 space-y-0.5">
                  {shippingResult.conflicts.map((c, i) => (
                    <li key={i}>
                      {c.country}{c.state ? ` / ${c.state}` : ''} — kept ₹{c.kept}, ignored ₹{c.dropped}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-1 mb-2">
              {[['current', 'Current card'], ['changes', 'Change history'], ['uploads', `Uploads (${shippingImports.length})`]].map(([k, label]) => (
                <button key={k} onClick={() => setShippingView(k)}
                        className={`px-2.5 py-1 rounded text-[11px] font-medium ${shippingView === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {label}
                </button>
              ))}
              {(shippingView === 'current' || shippingView === 'changes') && (
                <input value={shippingSearch} onChange={(e) => setShippingSearch(e.target.value)}
                       placeholder="Filter country / state…"
                       className="ml-auto px-2 py-1 border border-gray-300 rounded text-[11px] w-44" />
              )}
            </div>

            {shippingView === 'current' && (
              shippingRates.length === 0 ? (
                <div className="py-6 text-center text-gray-500 border border-dashed border-gray-200 rounded">
                  No rate card uploaded for this vendor — Vendor Payables falls back to the
                  flat per-package rate.
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">Country</th>
                        <th className="text-left px-2 py-1.5">State</th>
                        <th className="text-left px-2 py-1.5">Shipper</th>
                        <th className="text-right px-2 py-1.5">₹ / parcel</th>
                        <th className="text-left px-2 py-1.5">Effective</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shippingRates
                        .filter((r) => !shippingSearch
                          || `${r.country} ${r.state || ''}`.toLowerCase().includes(shippingSearch.toLowerCase()))
                        .map((r) => (
                        <tr key={r.id} className={r.state == null ? 'bg-blue-50/40' : ''}>
                          <td className="px-2 py-1.5 font-medium">{r.country}</td>
                          <td className="px-2 py-1.5">
                            {r.state ?? <span className="text-blue-700 text-[10px] font-medium uppercase">country default</span>}
                          </td>
                          <td className="px-2 py-1.5">{r.shipper}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                            ₹{Number(r.rate_inr).toLocaleString('en-IN')}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">
                            {fmtEffective(r.effective_from, r.effective_to)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {shippingView === 'changes' && (
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Destination</th>
                      <th className="text-left px-2 py-1.5">Shipper</th>
                      <th className="text-right px-2 py-1.5">₹ / parcel</th>
                      <th className="text-left px-2 py-1.5">In force</th>
                      <th className="text-left px-2 py-1.5">Changed by</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shippingHistory
                      .filter((r) => !shippingSearch
                        || `${r.country} ${r.state || ''}`.toLowerCase().includes(shippingSearch.toLowerCase()))
                      .map((r, i) => (
                      <tr key={i} className={r.effective_to == null ? '' : 'text-gray-500 bg-gray-50/60'}>
                        <td className="px-2 py-1.5">
                          <span className="font-medium text-gray-800">{r.country}</span>
                          {r.state ? ` / ${r.state}` : <span className="text-blue-700 text-[10px] font-medium uppercase"> country default</span>}
                        </td>
                        <td className="px-2 py-1.5">{r.shipper}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                          ₹{Number(r.rate_inr).toLocaleString('en-IN')}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {fmtEffective(r.effective_from, r.effective_to)}
                          {r.effective_to == null && <span className="ml-1 text-emerald-700 text-[10px] font-medium uppercase">current</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">
                          {r.uploaded_by_name || '—'}
                          {r.uploaded_at && <span className="text-gray-400"> · {new Date(r.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {shippingView === 'uploads' && (
              shippingImports.length === 0 ? (
                <div className="py-6 text-center text-gray-500 border border-dashed border-gray-200 rounded">
                  No rate-card uploads yet for this vendor.
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">Uploaded</th>
                        <th className="text-left px-2 py-1.5">File</th>
                        <th className="text-left px-2 py-1.5">Effective from</th>
                        <th className="text-left px-2 py-1.5">By</th>
                        <th className="text-right px-2 py-1.5">Destinations</th>
                        <th className="text-right px-2 py-1.5">Conflicts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shippingImports.map((imp) => (
                        <tr key={imp.id}>
                          <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">
                            {new Date(imp.uploaded_at).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                          <td className="px-2 py-1.5 max-w-[16rem] truncate" title={imp.source_filename || ''}>
                            {imp.source_filename || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-medium">
                            {new Date(imp.effective_from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">{imp.uploaded_by_name || '—'}</td>
                          <td className="px-2 py-1.5 text-right">{imp.rows_imported}</td>
                          <td className={`px-2 py-1.5 text-right ${imp.conflicts?.length ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                            {imp.conflicts?.length || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
        {showReshipPolicy && (
          <ReshipPolicyPanel vendor={vendor} onClose={() => setShowReshipPolicy(false)} />
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : products.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg">
            No products for this vendor.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-left px-3 py-2">Brand</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-left px-3 py-2">Preferred</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2">
                      <div className="text-sm">{p.product_name}</div>
                      <div className="text-xs text-gray-500">{p.manufacturer}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{p.brand_name || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      {p.unit_price != null ? `${currencySymbol(p.currency)}${Number(p.unit_price).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={async () => {
                        try {
                          await vendorApi.setProductPreferred(p.id, !p.is_preferred);
                          toast.success(p.is_preferred ? 'Cleared preferred' : 'Marked as preferred vendor for this product');
                          // reload products via the parent's loader by toggling local state
                          // Reflect server's clear-then-set: any other row
                          // with the same (lower-trim) product name must
                          // lose its preferred flag locally.
                          const norm = (s) => (s || '').toLowerCase().trim();
                          setProducts((prev) => prev.map((x) =>
                            x.id === p.id
                              ? { ...x, is_preferred: !p.is_preferred }
                              : (norm(x.product_name) === norm(p.product_name)
                                  ? { ...x, is_preferred: false }
                                  : x)
                          ));
                        } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                      }}
                              title="Use this vendor as the default for this product at intake"
                              className={`text-xs px-2 py-0.5 rounded ${
                                p.is_preferred ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                                               : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                              }`}>
                        {p.is_preferred ? '★ Preferred' : '☆ Set preferred'}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => toggleAvail(p)}
                              className={`text-xs px-2 py-0.5 rounded ${
                                p.is_available ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                               : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}>
                        {p.is_available ? <><CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Available</>
                                        : <><XCircle className="w-3 h-3 inline mr-0.5" /> Unavailable</>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setPricingProd(p)}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-0.5 ml-auto">
                        <Edit2 className="w-3 h-3" /> Edit price
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {addProdOpen && (
        <AddProductModal isOpen={addProdOpen} onClose={() => setAddProdOpen(false)} onDone={load}
                         vendorId={vendor.id} />
      )}
      {pricingProd && (
        <PricingModal isOpen={!!pricingProd} onClose={() => setPricingProd(null)} onDone={load}
                      product={pricingProd} />
      )}
    </Modal>
  );
}

function AddProductModal({ isOpen, onClose, onDone, vendorId }) {
  const [form, setForm] = useState({ productName: '', brandName: '', manufacturer: '' });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.productName.trim()) { toast.error('Product name required'); return; }
    setSaving(true);
    try {
      await vendorApi.addProduct(vendorId, form);
      toast.success('Product added');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add product">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Product name *</span>
          <input value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Brand</span>
            <input value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Manufacturer</span>
            <input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PricingModal({ isOpen, onClose, onDone, product }) {
  // History-preserving since 2026-06-09 — `effectiveFrom` slots the new price
  // into the timeline (past, today, or future), closing the previous row
  // rather than overwriting it. Default = today.
  const [unitPrice, setUnitPrice] = useState(
    product?.unit_price != null ? String(product.unit_price) : ''
  );
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!unitPrice) { toast.error('Unit price is required'); return; }
    if (!effectiveFrom || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) {
      toast.error('Pick an effective-from date'); return;
    }
    setSaving(true);
    try {
      await vendorApi.addPricing(product.id, {
        unitPrice: parseFloat(unitPrice),
        currency: 'INR',
        shippingCost: 0,    // shipping is per-vendor, not per-product
        effectiveFrom,
      });
      toast.success('Price updated');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Price — ${product.product_name}`}>
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          Set the vendor price for this product in <strong>₹</strong>.
          The <em>effective from</em> date is when the new price takes over;
          older prices are preserved so vendor payments use the right price
          per dispatch date. Shipping is set once per vendor (on the vendor card), not per product.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Unit price (₹) *</span>
            <input type="number" step="0.01" value={unitPrice} autoFocus
                   onChange={(e) => setUnitPrice(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Effective from *</span>
            <input type="date" value={effectiveFrom}
                   onChange={(e) => setEffectiveFrom(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save price
          </button>
        </div>
      </div>
    </Modal>
  );
}
